import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { 
  MapPin, 
  Building2, 
  Award, 
  Eye,
  Filter,
  X,
  Maximize2,
  Minimize2,
  RefreshCw
} from 'lucide-react';

// Fix for default marker icons in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons based on certification level
const createCustomIcon = (certificationLevel) => {
  const colors = {
    'level_3_excellence': '#f59e0b', // Gold
    'level_2_commitment': '#3b82f6', // Blue
    'level_1_basic': '#10b981', // Green
    'none': '#9ca3af' // Gray
  };
  
  const color = colors[certificationLevel] || colors['none'];
  
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        border: 3px solid white;
        box-shadow: 0 2px 5px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <div style="
          transform: rotate(45deg);
          color: white;
          font-size: 14px;
          font-weight: bold;
        ">
          🏢
        </div>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30]
  });
};

// Get coordinates for an actor - uses real geocoded coords or returns null
const getActorCoordinates = (actor) => {
  // First check if we have real geocoded coordinates
  if (actor.latitude && actor.longitude) {
    return [actor.latitude, actor.longitude];
  }
  return null;
};

// Component to fit bounds
const FitBounds = ({ actors }) => {
  const map = useMap();
  
  useEffect(() => {
    const validActors = actors.filter(a => getActorCoordinates(a));
    if (validActors.length > 0) {
      const bounds = validActors.map(a => getActorCoordinates(a));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [actors, map]);
  
  return null;
};

const SocialActorMapView = ({ actors = [], onViewDetails, onBatchGeocode, isAdmin = false }) => {
  const [filterCertification, setFilterCertification] = useState('all');
  const [filterOrgType, setFilterOrgType] = useState('all');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);

  // Filter actors
  const filteredActors = actors.filter(actor => {
    if (filterCertification !== 'all' && actor.certification_level !== filterCertification) {
      return false;
    }
    if (filterOrgType !== 'all' && actor.organization_type !== filterOrgType) {
      return false;
    }
    return true;
  });

  // Count geocoded actors
  const geocodedCount = filteredActors.filter(a => a.latitude && a.longitude).length;
  const notGeocodedCount = filteredActors.length - geocodedCount;

  // Handle batch geocode
  const handleBatchGeocode = async () => {
    if (onBatchGeocode) {
      setIsGeocoding(true);
      await onBatchGeocode();
      setIsGeocoding(false);
    }
  };

  // Get unique organization types
  const orgTypes = [...new Set(actors.map(a => a.organization_type).filter(Boolean))];

  // Certification badge helper
  const getCertificationLabel = (level) => {
    const labels = {
      'level_3_excellence': '🏆 Level A - Excellence',
      'level_2_commitment': '⭐ Level B - Commitment',
      'level_1_basic': '✓ Level C - Basic',
      'none': '○ Not Certified'
    };
    return labels[level] || labels['none'];
  };

  // Greece center
  const greeceCenter = [39.0742, 21.8243];

  return (
    <Card className={`${isFullscreen ? 'fixed inset-4 z-50' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="h-5 w-5 text-blue-600" />
            Organization Map View
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-1" />
              Filters
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsFullscreen(!isFullscreen)}
            >
              {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            {isFullscreen && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFullscreen(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Filters */}
        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            <div>
              <Label className="text-xs">Certification Level</Label>
              <Select value={filterCertification} onValueChange={setFilterCertification}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Levels</SelectItem>
                  <SelectItem value="level_3_excellence">🏆 Level A</SelectItem>
                  <SelectItem value="level_2_commitment">⭐ Level B</SelectItem>
                  <SelectItem value="level_1_basic">✓ Level C</SelectItem>
                  <SelectItem value="none">Not Certified</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Organization Type</Label>
              <Select value={filterOrgType} onValueChange={setFilterOrgType}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {orgTypes.map(type => (
                    <SelectItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 flex items-end">
              <div className="text-sm text-gray-600">
                Showing <span className="font-semibold">{filteredActors.length}</span> of{' '}
                <span className="font-semibold">{actors.length}</span> organizations
              </div>
            </div>
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {/* Legend */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex flex-wrap gap-3">
            <span className="text-xs font-medium text-gray-600">Legend:</span>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-amber-700"></div>
              <span className="text-xs">Level A</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-blue-500"></div>
              <span className="text-xs">Level B</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-green-700"></div>
              <span className="text-xs">Level C</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full bg-gray-400"></div>
              <span className="text-xs">Not Certified</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">
              <span className="font-semibold text-green-700">{geocodedCount}</span> on map
              {notGeocodedCount > 0 && (
                <span className="ml-1">
                  (<span className="text-amber-600">{notGeocodedCount}</span> missing coordinates)
                </span>
              )}
            </span>
            {isAdmin && notGeocodedCount > 0 && onBatchGeocode && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBatchGeocode}
                disabled={isGeocoding}
                className="h-7 text-xs"
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${isGeocoding ? 'animate-spin' : ''}`} />
                {isGeocoding ? 'Geocoding...' : 'Geocode All'}
              </Button>
            )}
          </div>
        </div>
        
        {/* Map */}
        <div className={`rounded-lg overflow-hidden border ${isFullscreen ? 'h-[calc(100vh-250px)]' : 'h-[400px]'}`}>
          <MapContainer
            center={greeceCenter}
            zoom={6}
            style={{ height: '100%', width: '100%' }}
            scrollWheelZoom={true}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            
            <FitBounds actors={filteredActors} />
            
            {filteredActors.map(actor => {
              const coords = getActorCoordinates(actor);
              if (!coords) return null;
              
              return (
                <Marker
                  key={actor.id}
                  position={coords}
                  icon={createCustomIcon(actor.certification_level)}
                >
                  <Popup>
                    <div className="min-w-[200px]">
                      <div className="flex items-start gap-2 mb-2">
                        <Building2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <h3 className="font-semibold text-sm">{actor.organization_name}</h3>
                          <p className="text-xs text-gray-600 capitalize">
                            {actor.organization_type?.replace('_', ' ')}
                          </p>
                        </div>
                      </div>
                      
                      <div className="space-y-1 mb-3">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <MapPin className="h-3 w-3" />
                          {actor.city}, {actor.country}
                        </div>
                        <div className="flex items-center gap-1">
                          <Award className="h-3 w-3 text-amber-700" />
                          <span className="text-xs">{getCertificationLabel(actor.certification_level)}</span>
                        </div>
                        {actor.assessment_scores?.overall && (
                          <div className="text-xs">
                            <span className="text-gray-600">Score: </span>
                            <span className="font-semibold text-blue-600">{actor.assessment_scores.overall}%</span>
                          </div>
                        )}
                      </div>
                      
                      {actor.sdg_alignment && actor.sdg_alignment.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {actor.sdg_alignment.slice(0, 4).map(sdg => (
                            <Badge key={sdg} variant="outline" className="text-xs px-1.5 py-0">
                              SDG {sdg}
                            </Badge>
                          ))}
                          {actor.sdg_alignment.length > 4 && (
                            <Badge variant="outline" className="text-xs px-1.5 py-0">
                              +{actor.sdg_alignment.length - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                      
                      {onViewDetails && (
                        <Button
                          size="sm"
                          className="w-full h-7 text-xs"
                          onClick={() => onViewDetails(actor)}
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </Button>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default SocialActorMapView;
